import Link from "next/link";
import { LeerZustand } from "../leerzustand";
import { breiteKlasse, cx } from "./breite";

export interface ZielZeile {
  label: string;
  /** Absprung (Link); ohne href reiner Text. */
  href?: string;
  /** Fortschritt 0…100 (Balkenbreite). */
  prozent: number;
  /** Text rechts (z. B. „12 / 93“). */
  wertText: string;
}

const BALKEN =
  "flex flex-col justify-center rounded-full overflow-hidden bg-primary text-xs text-primary-foreground text-center whitespace-nowrap transition duration-500";

/**
 * Fortschritt je Zeile: Kartenhülle aus goal-progress-bar-card (ohne die zwei Dropdowns
 * des Originals, Download/Teilen ohne Funktion im Portal), je Zeile die Komponente
 * „Progress mit Titel“ (Beschriftung und Wert über einer durchgehenden Spur, damit auch
 * kleine Werte als Balken lesbar bleiben; Betreiber-Entscheid 05.10.).
 */
export function ZielKarte({
  id,
  titel,
  titelLeise,
  zeilen,
  fuss,
  leer,
}: {
  id: string;
  titel: string;
  /** Kartentitel im leisen Stil (`font-medium`) wie `SegmentStatusKarte`/`BalkenKarte`, für Seiten mit gemischten Karten (CMDB-Übersicht). */
  titelLeise?: boolean;
  zeilen: ZielZeile[];
  fuss?: { href: string; text: string };
  leer: { titel: string; text: string; aktion?: { href: string; text: string } };
}) {
  const titelId = `${id}-titel`;
  return (
    <>
      {/* Preline: data-display/data-display-sub-analytics-kpis/data-display-cat-progress-cards/goal-progress-bar-card */}
      <section aria-labelledby={titelId} className="flex flex-col bg-card border border-card-line shadow-xs rounded-xl">
        {/* Header */}
        <div className="p-5 pb-4 grid grid-cols-2 items-center gap-x-4">
          <div className="col-span-2">
            <h2 id={titelId} className={titelLeise ? "inline-block font-medium text-foreground" : "inline-block font-semibold text-lg text-foreground"}>
              {titel}
            </h2>
          </div>
        </div>
        {/* End Header */}

        {/* Body */}
        <div className="h-full p-5 pt-0 space-y-4">
          {zeilen.length === 0 ? (
            <LeerZustand titel={leer.titel} text={leer.text} aktion={leer.aktion} />
          ) : (
            <ul className="space-y-4">
              {zeilen.map((z) => (
                <li key={z.label}>
                  {/* Preline: component/progress/progress-title-label */}
                  <div>
                    <div className="mb-2 flex justify-between items-center gap-x-2">
                      <h3 className="text-sm font-medium text-foreground">
                        {z.href ? (
                          <Link className="hover:text-primary focus:outline-hidden focus:text-primary" href={z.href}>
                            {z.label}
                          </Link>
                        ) : (
                          z.label
                        )}
                      </h3>
                      <span className="text-sm text-foreground">{z.wertText}</span>
                    </div>
                    <div
                      className="flex w-full h-2 bg-surface-1 rounded-full overflow-hidden"
                      role="progressbar"
                      aria-label={z.label}
                      aria-valuenow={z.prozent}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div className={cx(BALKEN, breiteKlasse(z.prozent))} />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* End Body */}

        {/* Footer */}
        {fuss && (
          <div className="text-center border-t border-card-footer">
            <Link
              className="p-3 flex justify-center items-center gap-x-2 text-sm text-primary rounded-b-lg hover:text-primary-hover focus:outline-hidden focus:decoration-2 focus:underline focus:text-primary-focus"
              href={fuss.href}
            >
              {fuss.text}
            </Link>
          </div>
        )}
        {/* End Footer */}
      </section>
    </>
  );
}
